/**
 * Off-screen pictures of a plan (TD §10): the 320 px card picture for My plans, the History previews and
 * "Download picture" (about 2000 px). Always in the light map colours.
 */
import { analysePlan } from '../../core/analyse';
import { derive } from '../../core/geom';
import type { PlanDoc, Species } from '../../shared/types';
import { LIGHT } from './palette';
import { drawScene } from './renderer';
import { makeTransform } from './view';

/** Size of the card picture (TD §10). */
export const THUMB_PX = 320;
/** Size of the downloaded picture (TD §10). */
export const PICTURE_PX = 2000;

/** Options for renderPlan. */
export interface PictureOptions {
  species?: readonly Species[] | null;
  /** The screenshot under the map, if it should be included. */
  overlayImage?: HTMLImageElement | null;
  grid?: boolean;
  labels?: boolean;
  scaleBar?: boolean;
}

/** Draws the whole plan on a new px×px canvas. */
export function renderPlanCanvas(doc: PlanDoc, px: number, o: PictureOptions = {}): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = px;
  const g = canvas.getContext('2d');
  if (!g) return canvas;
  const derived = derive(doc);
  const species = o.species ?? null;
  const analysis = species ? analysePlan(doc, derived, [...species], doc.settings.extraSpacePct) : null;
  // drawn as a 1000 px map scaled up, so lines, icons and labels keep the on-screen proportions
  const scale = Math.max(1, px / 1000), css = px / scale;
  const t = makeTransform(css, css, doc.size, 1, doc.size / 2, doc.size / 2);
  drawScene(g, t, scale, {
    doc,
    derived,
    analysis,
    species: species ? new Map(species.map((s) => [s.id, s])) : null,
    palette: LIGHT,
    selection: null,
    handles: false,
    grips: false,
    showGrid: o.grid ?? false,
    overlayImage: o.overlayImage ?? null,
    labels: o.labels ?? true,
    scaleBar: o.scaleBar ?? true,
  });
  return canvas;
}

/** PNG of the plan, px×px. */
export function renderPlanPng(doc: PlanDoc, px: number, o: PictureOptions = {}): Promise<Blob | null> {
  const canvas = renderPlanCanvas(doc, px, o);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

/** The 320 px card picture for My plans (no labels: they'd be unreadable that small). */
export function renderThumbPng(doc: PlanDoc, species?: readonly Species[] | null): Promise<Blob | null> {
  return renderPlanPng(doc, THUMB_PX, { species, labels: false, scaleBar: false });
}

/** Loads an image (e.g. the plan's screenshot) for drawing; null if it can't be loaded. */
export function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Saves a blob as a file download. */
export function saveBlob(blob: Blob, fileName: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

/** A safe file name from a plan name: "My zoo 15 Oct.png". */
export function pictureFileName(name: string): string {
  const clean = name.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Zoo plan';
  return `${clean}.png`;
}
